import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { fetchDevLoginOptions, startDevLogin, startGoogleLogin, type DevLoginOptions } from '@/api/auth';

import styles from './Login.module.css';

// Google OAuth 로그인 진입 페이지.
// callback이 실패한 경우 ?error=... 로 redirect 되어 와서 메시지를 표시한다.
// 성공한 사용자는 곧바로 originally-requested path로 callback에서 redirect 되므로
// 이 컴포넌트는 보통 거의 보지 못한다.
export default function LoginPage() {
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const errorMessage = searchParams.get('error');
  const returnTo = searchParams.get('return_to') ?? undefined;

  // 로컬 전용 테스트 로그인 옵션(프로덕션은 enabled=false → 미노출).
  const [dev, setDev] = useState<DevLoginOptions | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetchDevLoginOptions(ctrl.signal)
      .then(setDev)
      .catch(() => setDev(null));
    return () => ctrl.abort();
  }, []);

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <div className={styles.brandLogo}>S</div>
          <div>
            <div className={styles.brandName}>Supercent</div>
            <div className={styles.brandSub}>BigQuery 접근 관리</div>
          </div>
        </div>

        <h1 className={styles.heading}>로그인이 필요해요</h1>
        <p className={styles.sub}>
          supercent.io 구글 계정으로만 접근할 수 있어요.
        </p>

        {errorMessage && <div className={styles.error}>{errorMessage}</div>}

        <button
          type="button"
          className={styles.button}
          onClick={() => startGoogleLogin(returnTo)}
        >
          <GoogleIcon />
          Google 계정으로 계속하기
        </button>

        <p className={styles.note}>
          로그인하면 세션 쿠키가 12시간 동안 유지됩니다.
        </p>

        {dev?.enabled && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#8a4b00', marginBottom: 8 }}>
              🧪 로컬 테스트 로그인 (권한별 QA)
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {dev.accounts.map((account) => (
                <button
                  key={account.email}
                  type="button"
                  onClick={() => startDevLogin(account.email, returnTo)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    border: '1px solid #e2e2e2',
                    borderRadius: 8,
                    background: '#fff',
                    cursor: 'pointer',
                    font: '600 13px/1.2 Pretendard, sans-serif',
                  }}
                >
                  <span>{account.label}</span>
                  <span style={{ color: '#9b9b9b', fontFamily: 'monospace', fontSize: 11 }}>
                    {account.email}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg className={styles.googleIcon} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.85 0-5.27-1.93-6.13-4.52H2.18v2.84A11 11 0 0 0 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.87 14.12A6.59 6.59 0 0 1 5.5 12c0-.74.13-1.46.37-2.12V7.04H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.96l3.69-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.07.56 4.21 1.65l3.15-3.15C17.45 2.08 14.97 1 12 1A11 11 0 0 0 2.18 7.04l3.69 2.84C6.73 7.3 9.15 5.38 12 5.38z"
      />
    </svg>
  );
}
